import { useEffect, useMemo, useRef, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { useDebounceFn } from 'ahooks';
import { cn } from '@rocco/utils/cn';
import { Input } from '@rocco/components/input';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { MeUser } from '~/server/types';
import type { BuildHubProject, Conversation } from '../../types';
import { filterChats } from '../../utils';
import { ChatInboxFilter } from './ChatInboxFilter';
import {
    ChatInboxListItem,
    ChatInboxListItemSkeleton,
} from './ChatInboxListItem';

/**
 * The chat inbox list. This is an infinite scrolling list of conversations
 * belonging to the current user.
 * @see {@link ChatInboxDrawer}
 */
export function ChatInboxList({
    className,
    conversations,
    user,
    isLoading = false,
    hasMore = false,
    onFiltersChange,
    onLoadMore,
}: {
    className?: string;
    conversations: Conversation[];
    user: MeUser;
    isLoading?: boolean;
    hasMore?: boolean;
    onFiltersChange?: (filters: {
        search: string;
        isArchived: boolean;
        projectIDs: string[];
    }) => void;
    onLoadMore?: () => void;
}) {
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'archived'>('all');
    const [selectedProjects, setSelectedProjects] = useState<BuildHubProject[]>(
        []
    );
    const filteredConversations = useMemo(() => {
        return filterChats({
            conversations,
            userID: user.ID,
            search,
            projectIDs: selectedProjects.map((project) => project.ID),
        });
    }, [conversations, search, selectedProjects, user.ID]);
    const {
        run: setSearchDebounced,
        flush: flushSearch,
        cancel: cancelSearch,
    } = useDebounceFn(
        (search: string) => {
            handleFiltersChange({ search });
        },
        { wait: 300 }
    );

    function handleFiltersChange({
        search: newSearch,
        status,
        projects,
    }: {
        search?: string;
        status?: 'all' | 'archived';
        projects?: BuildHubProject[];
    }) {
        cancelSearch();
        if (status !== undefined) {
            setStatusFilter(status);
        }
        if (projects !== undefined) {
            setSelectedProjects(projects);
        }
        onFiltersChange?.({
            search: newSearch ?? search,
            isArchived: (status ?? statusFilter) === 'archived',
            projectIDs: (projects ?? selectedProjects).map(
                (project) => project.ID
            ),
        });
    }

    // Scroll to top when the filters change.
    useEffect(() => {
        scrollAreaRef.current?.scrollTo({
            top: 0,
            behavior: 'instant',
        });
    }, [search, statusFilter, selectedProjects]);

    // Trigger load more conversations when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollArea = scrollAreaRef.current;
        if (!sentinel || !scrollArea || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    entry?.isIntersecting &&
                    onLoadMore &&
                    hasMore &&
                    !isLoading
                ) {
                    onLoadMore();
                }
            },
            { root: scrollArea }
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, onLoadMore]);

    return (
        <section className={cn('flex flex-col gap-6', className)}>
            <div className="flex items-center gap-2">
                <Input
                    className="h-12 rounded-full"
                    containerClassName="min-w-0 flex-1"
                    color="contrast"
                    placeholder="Search messages"
                    showClearButton
                    postfix={<SearchIcon className="size-5 opacity-50" />}
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setSearchDebounced(e.target.value.trim());
                        if (!e.target.value.trim()) {
                            flushSearch();
                        }
                    }}
                />
                <ChatInboxFilter
                    statusFilter={statusFilter}
                    selectedProjects={selectedProjects}
                    onStatusFilterChange={(status) =>
                        handleFiltersChange({ status })
                    }
                    onProjectsChange={(projects) =>
                        handleFiltersChange({ projects })
                    }
                />
            </div>
            <ScrollArea
                ref={scrollAreaRef}
                className={cn(
                    'min-h-0 flex-1',
                    // Fix for width overflow issue.
                    // https://github.com/radix-ui/primitives/issues/2722
                    '[&>div>div]:!block'
                )}
                scrollBarClassName="w-2 translate-x-2"
                thumbClassName="bg-gray-500"
            >
                <ul
                    className="relative flex flex-col gap-2"
                    aria-label="Chat inbox"
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    {filteredConversations.length === 0 && isLoading
                        ? Array.from({ length: 8 }).map((_, index) => (
                              <ChatInboxListItemSkeleton key={index} />
                          ))
                        : filteredConversations.map((conversation) => (
                              <ChatInboxListItem
                                  key={conversation.ID}
                                  conversation={conversation}
                              />
                          ))}
                    {hasMore && filteredConversations.length > 0 && (
                        <div
                            ref={loadMoreSentinelRef}
                            className="invisible absolute inset-x-0 bottom-120 h-px"
                            aria-hidden="true"
                        ></div>
                    )}
                </ul>
            </ScrollArea>
        </section>
    );
}
